import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Search, ChevronDown, Eye } from "lucide-react";
import FilterModal from "./FilterModel";
import { FILTER_ACCESS_CONFIG } from "../../config/filterAccess.config";
import { canAccessFilter } from "../../utils/featureAccess";
import { useBrandAuthCheck } from "../../hooks/useBrandAuthCheck";
import RegisterModal from "../../components/RegisterModal";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearUserState } from "../../Redux/userSlice";

const CATEGORIES = [
    "Lifestyle", "Beauty", "Fashion", "Travel", "Health & Fitness",
    "Family & Children", "Food & Drink", "Comedy & Entertainment",
    "Art & Photography", "Music & Dance", "Animals & Pets", "Model",
    "Adventure & Outdoors", "Education", "Entrepreneur & Business",
    "Athlete & Sports", "Technology", "Gaming", "Healthcare", "LGBTQ2+"
];

const Filters = ({ filters, setFilters, planType = "basic", showRemainingViews = false, viewsRemaining = 0, viewLimit = 0 }) => {
    const { isBrand, isAuthenticated } = useBrandAuthCheck();
    const [showJoinModal, setShowJoinModal] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [showPlatform, setShowPlatform] = useState(false);
    const [showCategory, setShowCategory] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const genderButtonRef = useRef(null);
    const ageButtonRef = useRef(null);
    const priceButtonRef = useRef(null);
    const contentTypeButtonRef = useRef(null);
    const ethnicityButtonRef = useRef(null);
    const followersButtonRef = useRef(null);
    const locationButtonRef = useRef(null);
    const languageButtonRef = useRef(null);
    const verifiedButtonRef = useRef(null);
    const [categorySearch, setCategorySearch] = useState("");
    const [searchInput, setSearchInput] = useState(filters.search || "");

    const canUseMarketplaceFilters = isBrand && isAuthenticated;
    const handleRegisterAs = useCallback(
        (type) => {
            setShowJoinModal(false);
            if (type === "influencer") {
                navigate("/influencer/login");
                return;
            }

            const userType = localStorage.getItem("userType");
            if (userType === "influancer") {
                dispatch(clearUserState());
            }
            navigate("/company/login");
        },
        [dispatch, navigate]
    );

    const requireBrandForInteraction = useCallback(() => {
        if (!canUseMarketplaceFilters) {
            setShowJoinModal(true);
            return false;
        }
        return true;
    }, [canUseMarketplaceFilters]);

    // Derive active pills from actual filter values
    const getActivePills = useCallback(() => {
        const active = [];
        if (filters.search && String(filters.search).trim()) {
            active.push("search");
        }
        if (filters.contentType && filters.contentType.length > 0) {
            active.push("contentType");
        }
        if (filters.followers && (filters.followers.min !== 0 || filters.followers.max !== 100000000)) {
            active.push("followers");
        }
        if (filters.location && Object.keys(filters.location).length > 0 &&
            (filters.location.country || filters.location.region || filters.location.city)) {
            active.push("location");
        }
        if (filters.price != null && filters.price !== "") {
            active.push("price");
        }
        if (filters.gender && filters.gender !== "") {
            active.push("gender");
        }
        if (filters.language && filters.language !== null) {
            active.push("language");
        }
        if (filters.age && filters.age !== null) {
            active.push("age");
        }
        if (filters.ethnicity && filters.ethnicity !== null) {
            active.push("ethnicity");
        }
        if (filters.verified === true) {
            active.push("verified");
        }
        return active;
    }, [filters]);

    const activePills = useMemo(() => getActivePills(), [getActivePills]);


    const wrapperRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowPlatform(false);
                setShowCategory(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (!canUseMarketplaceFilters) return;

        const timer = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                search: searchInput
            }));
        }, 500); // wait 500ms after typing stops

        return () => clearTimeout(timer);
    }, [searchInput, setFilters, canUseMarketplaceFilters]);

    const INITIAL_FILTERS = {
        search: "",
        platform: "",
        category: "",
        contentType: [],
        followers: { min: 0, max: 100000000 },
        location: {},
        gender: "",
        price: null,
        language: null,
        age: null,
        ethnicity: null,
        verified: null,
    };

    const handleClearAll = () => {
        if (!requireBrandForInteraction()) return;
        setFilters(INITIAL_FILTERS);
        setActiveModal(null);
    };

    const filteredCategories = useMemo(() => {
        if (!categorySearch) return CATEGORIES;
      
        return CATEGORIES.filter((c) =>
          c.toLowerCase().includes(categorySearch.toLowerCase())
        );
      }, [categorySearch]);

    const handleOpenFilter = useCallback(
        (filterName) => {
            if (!requireBrandForInteraction()) return;
            const nextType = canAccessFilter(filterName, planType) ? filterName : "premium";
            setActiveModal(nextType);
        },
        [planType, requireBrandForInteraction]
    );

    const filterPills = useMemo(
        () => ([
            { name: "contentType", label: "Content Type" },
            { name: "followers", label: "Followers" },
            { name: "location", label: "Location" },
            { name: "price", label: "Price" },
            { name: "gender", label: "Gender" },
            { name: "language", label: "Language" },
            { name: "age", label: "Age" },
            { name: "ethnicity", label: "Ethnicity" },
            { name: "verified", label: "Verified" },
        ]),
        []
    );

    return (
        <>
            <div ref={wrapperRef} className="w-full">
                {/* ================= SEARCH BAR - centered; on desktop same row as views left ================= */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-center lg:gap-4 w-full max-w-4xl mx-auto relative">
                    <div className="relative bg-white rounded-full shadow flex items-center gap-2 sm:gap-3 md:gap-4 w-full max-w-2xl sm:max-w-3xl lg:max-w-none lg:flex-1 min-w-0 px-3 py-2 sm:px-4 sm:py-2.5 md:py-3 min-h-[2.75rem] sm:min-h-[3.25rem] md:min-h-[3.5rem]">
                        {/* Search */}
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase tracking-wide">Search</p>
                            <input
                                type="text"
                                placeholder="Search by name or username"
                                value={searchInput || ""}
                                onFocus={() => requireBrandForInteraction()}
                                onClick={() => requireBrandForInteraction()}
                                onChange={(e) => {
                                    if (!requireBrandForInteraction()) return;
                                    setSearchInput(e.target.value);
                                }}
                                onKeyDown={(e) => e.key === "Enter" && (e.target.blur || (() => { }))()}
                                className="w-full font-medium truncate bg-transparent border-none outline-none text-xs sm:text-sm placeholder-gray-400 h-6 sm:h-7"
                            />
                        </div>
                        <div className="h-6 sm:h-8 md:h-9 w-px bg-gray-200 flex-shrink-0" />
                        {/* Platform - half */}
                        <div
                            className="flex-1 min-w-0 cursor-pointer text-center sm:text-left hover:text-gray-700 transition-colors"
                            onClick={() => {
                                if (!requireBrandForInteraction()) return;
                                setShowPlatform(!showPlatform);
                                setShowCategory(false);
                            }}
                        >
                            <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase tracking-wide">Platform</p>
                            <p className="font-medium truncate text-xs sm:text-sm">{filters.platform || "All"}</p>
                        </div>

                        <div className="h-6 sm:h-8 md:h-9 w-px bg-gray-200 flex-shrink-0" />

                        {/* Category - half */}
                        <div
                            className="flex-1 min-w-0 cursor-pointer text-center sm:text-left hover:text-gray-700 transition-colors"
                            onClick={() => {
                                if (!requireBrandForInteraction()) return;
                                setShowCategory(!showCategory);
                                setShowPlatform(false);
                            }}
                        >
                            <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase tracking-wide">Category</p>
                            <p className="text-gray-400 truncate text-xs sm:text-sm">
                                {filters.category || "Enter niches..."}
                            </p>
                        </div>

                        <button className="bg-black text-white rounded-full p-2 sm:p-2.5 md:p-3 flex-shrink-0 h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 flex items-center justify-center" aria-label="Search">
                            <Search size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </button>

                        {/* Platform dropdown */}
                        {showPlatform && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white rounded-xl shadow-lg w-56 sm:w-60 p-3 z-50">
                                {["Instagram", "YouTube"].map((p) => (
                                    <div
                                        key={p}
                                        onClick={() => {
                                            if (!requireBrandForInteraction()) return;
                                            setFilters({ ...filters, platform: p, contentType: [] });
                                            setShowPlatform(false);
                                        }}
                                        className="px-4 py-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                                    >
                                        {p}
                                    </div>
                                ))}
                            </div>
                        )}

                        {showCategory && (
                            <div className="absolute left-0 right-0 top-[calc(100%+0.75rem)] mx-auto mt-3 bg-white rounded-2xl shadow-xl p-6 max-w-[680px] z-50">

                                {/* Search input */}
                                <input
                                    type="text"
                                    placeholder="Search category..."
                                    className="w-full border rounded-lg px-4 py-2 mb-4 outline-none focus:ring-2 focus:ring-black"
                                    onChange={(e) => setCategorySearch(e.target.value)}
                                />

                                <p className="text-sm text-gray-400 mb-4">Popular</p>

                                <div className="flex flex-wrap gap-2 sm:gap-3">
                                    {filteredCategories.map((cat) => (
                                        <button
                                            key={cat}
                                            onClick={() => {
                                                if (!requireBrandForInteraction()) return;
                                                setFilters({ ...filters, category: cat });
                                                setShowCategory(false);
                                            }}
                                            className="px-3 sm:px-4 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>

                            </div>
                        )}
                    </div>
                    {/* Views left: same row as search on desktop only */}
                    {showRemainingViews && (
                        <div className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 border border-gray-200 text-sm font-medium text-gray-700 flex-shrink-0">
                            <Eye size={16} className="shrink-0" />
                            <span className="whitespace-nowrap">
                                {viewsRemaining} / {viewLimit} views left today
                            </span>
                        </div>
                    )}
                </div>

                {/* ================= FILTER PILLS - responsive sizes, scrollable on small/medium ================= */}
                {/* ================= FILTER PILLS ================= */}
                <div className="w-full">
                    <div
                        className="flex gap-2 sm:gap-3 items-center overflow-x-auto overflow-y-visible pb-2 pt-2 flex-nowrap scroll-smooth px-2 sm:px-6 md:px-10 lg:px-16" >
                        {filterPills.map(({ name, label }) => {
                            const isPremium = FILTER_ACCESS_CONFIG?.[name] === "premium";
                            const pill = (
                                <FilterPill
                                    key={name}
                                    label={label}
                                    premium={isPremium}
                                    isActive={activePills.includes(name)}
                                    onClick={() => handleOpenFilter(name)}
                                />
                            );

                            if (name === "gender") {
                                return (
                                    <div key={name} ref={genderButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }

                            if (name === "age") {
                                return (
                                    <div key={name} ref={ageButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }

                            if (name === "price") {
                                return (
                                    <div key={name} ref={priceButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }
                            if (name === "contentType") {
                                return (
                                    <div key={name} ref={contentTypeButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }
                            if (name === "ethnicity") {
                                return (
                                    <div key={name} ref={ethnicityButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }
                            if (name === "followers") {
                                return (
                                    <div key={name} ref={followersButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }
                            if (name === "location") {
                                return (
                                    <div key={name} ref={locationButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }
                            if (name === "language") {
                                return (
                                    <div key={name} ref={languageButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }
                            if (name === "verified") {
                                return (
                                    <div key={name} ref={verifiedButtonRef}>
                                        {pill}
                                    </div>
                                );
                            }

                            return pill;
                        })}

                        <button
                            onClick={handleClearAll}
                            className="text-xs sm:text-sm underline text-orange-500 hover:text-orange-600 whitespace-nowrap flex-shrink-0 ml-2"
                        >
                            Clear All
                        </button>
                    </div>
                </div>

            </div>

            {/* ================= MODAL ================= */}
            {activeModal && (
                <FilterModal
                    type={activeModal}
                    filters={filters}
                    setFilters={setFilters}
                    platform={filters.platform}
                    onClose={() => setActiveModal(null)}
                    buttonRef={
                        activeModal === "gender"
                            ? genderButtonRef
                            : activeModal === "age"
                            ? ageButtonRef
                            : activeModal === "price"
                            ? priceButtonRef
                            : activeModal === "contentType"
                            ? contentTypeButtonRef
                            : activeModal === "ethnicity"
                            ? ethnicityButtonRef
                            : activeModal === "followers"
                            ? followersButtonRef
                            : activeModal === "location"
                            ? locationButtonRef
                            : activeModal === "language"
                            ? languageButtonRef
                            : activeModal === "verified"
                            ? verifiedButtonRef
                            : null
                    }
                />
            )}

            <RegisterModal
                isOpen={showJoinModal}
                onClose={() => setShowJoinModal(false)}
                onRegisterAs={handleRegisterAs}
            />
        </>
    );
};


const FilterPill = ({ label, premium, onClick, isActive }) => (
    <div className="relative inline-flex pt-2 sm:pt-3 flex-shrink-0">
        {/* Premium badge */}
        {premium && (
            <span
                className="absolute -top-0.5 -right-2.5 text-white font-semibold text-[10px] px-1.5 py-0.5 rounded-full
                          bg-[linear-gradient(90deg,rgb(255,132,160)_0%,rgb(251,102,157)_20%,rgb(179,45,194)_95%,rgb(136,95,183)_100%)] whitespace-nowrap" >
                Premium
            </span>
        )}

        {/* Pill button - responsive size by screen */}
        <button
            type="button"
            onClick={onClick}
            className={`
                border rounded-full px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2
                transition-all whitespace-nowrap
                ${isActive
                    ? "border-black bg-gray-200 text-black"
                    : "bg-white hover:bg-gray-50 border-gray-300"
                }
              `}
        >
            {label}
            <ChevronDown size={12} className="sm:w-3.5 sm:h-3.5 shrink-0" />
        </button>
    </div>
);


export default Filters;